/**
 * La ficha y su firma (ficha.html, ADR-14 pts 2–6). La ficha se LEE entera
 * (el texto vive en git y se cambia por el método, nunca aquí); al lado, lo
 * que el socio hace:
 * - «Tu criterio» (firma `criterio`, solo el dueño del cuaderno): una
 *   respuesta por pregunta `[CRITERIO PENDIENTE]`, la VISTA PREVIA siempre a
 *   la vista (la compone la API: es lo que se firma), «Vale, así lo hacemos»
 *   manteniendo pulsado (la firma no se edita: se añade otra), «No lo sé»
 *   (también es una firma: la pregunta sigue abierta) y «Así no» con «Qué
 *   cambiarías» obligatorio;
 * - «Revisión» (un segundo socio, solo sobre una ficha de la casa) y, con el
 *   rol completo, el visto del despacho y la resolución de un «así no»;
 * - «Firmas»: hash leído → hash firmado, vigente / sustituida / caducada,
 *   pendiente de volcar / en PR / volcada. Dos verdades a la vista: el estado
 *   de git y la firma por volcar.
 * Estados de página `?estado=` (cargando · error · bloqueada) y de firma
 * `?firma=` (revisión: ver `simular.ts`). Un 409 dice que la ficha cambió y
 * ofrece volver a leerla; un 403, el porqué.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ApiError } from "@skein/api-client";
import { ANSWER_MAX, NO_LO_SE, PR_URL, type Biblioteca, type Ficha as FichaData, type FirmaRequest, type Firma } from "@skein/contracts";
import {
  Avatar, Badge, Banner, Button, Card, CardHeader, CardPad, Empty, Fainted, Feed, FeedItem, FeedWho, Field, FieldGroup, FichaProse, FormActs,
  Help, Hold, Icon, ItemRow, LiveRegion, Mono, PageHead, Prose, ProseP, Skeleton, Stale, Tab, Tip, TabPanel, Tabs, TextField, Textarea, Whisper, buttonClass, useToast,
} from "@skein/ui";
import { initials } from "../../auth/initials";
import { useAsideSlot } from "../../shell/asideSlot";
import { useCrumbs } from "../../shell/crumbSlot";
import { useBibliotecaDe, useCuadernoDe, useDueno, useFichaDe, useFirmar, useResolver, useTelemetriaDe, useVistaPrevia, type BibliotecaApi, type TelemetriaApi } from "../../api/biblioteca";
import { CLASE, ESTADO_GIT, claseFirma, cuando, elDia, veredicto, licencia, porqueHumano, sinGuiones, decisionFirma, esNoLoSe, estadoCriterio, fechaDia, hash8, motivo403, parrafosCriterio, type EstadoCriterio } from "./logica";
import { MiRevision } from "./MiRevision";
import { RESPUESTA_LARGA, esFirmaForzada, simularFirma } from "./simular";

const FIRMAS_ID = "firmas";
const focoFirmas = () => window.setTimeout(() => document.getElementById(FIRMAS_ID)?.focus(), 0);
const SIN_CONEXION_FIRMA = "Sin conexión: ahora no se puede firmar. Tu respuesta no se pierde.";
const FORZADA = "Vista forzada (?firma=): aquí no se firma de verdad.";
const AVISO_PUBLICA = "Tu respuesta se publica tal cual en el asesor para todos los clientes: sin nombres ni cifras de tu empresa (ADR-17).";
/** «Ventas netas» → «ventas netas» dentro de una frase: solo la inicial (un nombre propio se respeta en el resto). */
const inicialMinuscula = (s: string) => (/^\p{Lu}\p{Ll}/u.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s);
/**
 * Las notas del método citan otras fichas (y Skills) por su slug. Un slug
 * empieza por letra (así una fecha `2026-10-19` no lo es): si se resuelve a
 * una relacionada, enlace con su título; si no (un Skill, una ficha que no es
 * relacionada), su nombre sin guiones. La nota nunca se queda en crudo.
 */
export function notaConEnlaces(nota: string, titulos: ReadonlyMap<string, string>): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of nota.matchAll(/\b[a-z][a-z0-9]*(?:-[a-z0-9]+)+\b/g)) {
    out.push(nota.slice(last, m.index));
    const t = titulos.get(m[0]);
    out.push(t !== undefined
      ? <Link key={m.index} to="/conocimiento/$slug" params={{ slug: m[0] }}>{inicialMinuscula(t)}</Link>
      : sinGuiones(m[0]));
    last = m.index + m[0].length;
  }
  out.push(nota.slice(last));
  return out;
}
/** «¿confirmas…?» → «¿Confirmas…?»: la pregunta del marcador, como frase. */
const mayuscula = (s: string) => s.replace(/^([¿¡«"]*)(\p{Ll})/u, (_m, a: string, b: string) => a + b.toUpperCase());
const hoy = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());

function Cargando() {
  const ln = (w: string, mt?: string) => <Skeleton style={{ height: ".9em", width: w, ...(mt ? { marginTop: mt } : {}) }} />;
  return (
    <section aria-busy="true" aria-label="Cargando la ficha">
      <PageHead title="" skeleton />
      <div className="cols cols--ficha">
        <div className="stack">
          <Card>
            <Skeleton style={{ height: "2.5rem", width: "40%" }} />
            {ln("30%", "var(--sk-sp-6)")}{ln("100%", "var(--sk-sp-3)")}{ln("92%", "var(--sk-sp-2)")}{ln("80%", "var(--sk-sp-2)")}
            {ln("35%", "var(--sk-sp-6)")}{ln("100%", "var(--sk-sp-3)")}{ln("88%", "var(--sk-sp-2)")}{ln("60%", "var(--sk-sp-2)")}
          </Card>
        </div>
        <div className="stack firma-col">
          <Card>{ln("45%")}{ln("100%", "var(--sk-sp-3)")}{ln("70%", "var(--sk-sp-2)")}<Skeleton style={{ height: "4.5rem", marginTop: "var(--sk-sp-4)" }} /></Card>
          <Card>{ln("40%")}{ln("85%", "var(--sk-sp-3)")}<Skeleton style={{ height: "2rem", width: "50%", marginTop: "var(--sk-sp-3)" }} /></Card>
        </div>
      </div>
    </section>
  );
}

/**
 * Lo que pasa con un error de firma, POR CÓDIGO de la API (nunca por el
 * texto): 409 `stale` → la ficha cambió; 409 `taken`/`resolved` → su
 * porqué; 403 → el porqué; 400 → el mensaje; si no, la red. El aviso vive
 * en una región `role=status` montada desde el principio (se anuncia) y
 * recibe el foco al fallar.
 */
function useErrores(onStale: () => void) {
  const say = useToast();
  const [aviso, setAvisoState] = useState<string | null>(null);
  const region = useRef<HTMLDivElement>(null);
  const setAviso = (m: string | null) => { setAvisoState(m); if (m) window.setTimeout(() => region.current?.focus(), 0); };
  const trata = (e: unknown) => {
    if (e instanceof ApiError && e.status === 409 && e.code === "stale") { onStale(); return; }
    if (e instanceof ApiError && e.status === 409 && e.code === "taken") { setAviso("Otro socio se adelantó: el cuaderno ya tiene dueño."); return; }
    if (e instanceof ApiError && e.status === 409 && e.code === "resolved") { setAviso("Ese «así no» ya estaba resuelto: vuelve a cargar la ficha."); return; }
    if (e instanceof ApiError && e.status === 403) { setAviso(motivo403(e.code, e.message)); return; }
    if (e instanceof ApiError && (e.status === 400 || e.status === 409)) { setAviso(porqueHumano(e.message)); return; }
    say("No llego a la biblioteca: tu respuesta no se pierde. Prueba otra vez en un momento.");
  };
  const avisoUi = <LiveRegion ref={region}>{aviso && <Banner tone="warn">{aviso}</Banner>}</LiveRegion>;
  return { aviso, setAviso, trata, avisoUi };
}

interface Comun {
  api: BibliotecaApi;
  f: FichaData;
  me: { oid: string; name: string };
  enError: boolean;
  forzada: boolean;
  onStale: () => void;
}

// ── 1 · Tu criterio ───────────────────────────────────────────────────

function respuestas(e: EstadoCriterio, n: number): string[] {
  const de = e.k === "caducada" || e.k === "firmada" || e.k === "volcada" || e.k === "nolose" ? e.firma.answers : [];
  return Array.from({ length: n }, (_, i) => { const a = de[i] ?? ""; return esNoLoSe(a) ? "" : a; });
}

function TuCriterio({ api, f, me, enError, forzada, onStale, invalidaForzada }: Comun & { invalidaForzada: boolean }) {
  const say = useToast();
  const est = estadoCriterio(f, me.oid);
  const firmar = useFirmar(api, f.slug);
  const vista = useVistaPrevia(api, f.slug);
  // la de «No lo sé» va aparte: no pisa la vista previa de lo escrito
  const vistaNoSe = useVistaPrevia(api, f.slug);
  const dueno = useDueno(api);
  const { setAviso, trata, avisoUi } = useErrores(onStale);
  const [editando, setEditando] = useState(false);
  const n = Math.max(1, f.preguntas.length);
  const [answers, setAnswers] = useState<string[]>(() => (invalidaForzada ? Array.from({ length: n }, () => RESPUESTA_LARGA) : respuestas(est, n)));
  const [errores, setErrores] = useState<(string | undefined)[]>([]);
  const [asino, setAsino] = useState<{ open: boolean; texto: string; error?: string | undefined }>({ open: false, texto: "" });
  const [enviando, setEnviando] = useState(false);
  const estadoRef = useRef<HTMLDivElement>(null);
  const asinoRef = useRef<HTMLTextAreaElement>(null);

  const mia = "mia" in est && est.mia;
  const formVis = est.k === "sin-firmar" || est.k === "caducada" || (editando && f.puedoFirmar.criterio);
  const limpias = answers.map((a) => a.trim());
  const listo = limpias.every(Boolean);
  const clave = JSON.stringify([f.hash, ...limpias]);

  // la vista previa: la compone la API (lo que se firma), al dejar de escribir
  useEffect(() => {
    if (!formVis || !listo) return;
    const t = window.setTimeout(() => vista.mutate(limpias, { onSettled: () => setPara(clave) }), 400);
    return () => window.clearTimeout(t);
    // `vista` cambia de identidad en cada render; manda lo escrito
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, formVis]);
  // a qué texto (hash de la ficha + respuestas) corresponde la última vista previa
  const [para, setPara] = useState<string | null>(null);
  const pv = vista.data && para === clave && JSON.stringify(vista.variables) === JSON.stringify(limpias) ? vista.data : undefined;
  const pvFallo = vista.isError && para === clave ? (vista.error instanceof ApiError ? porqueHumano(vista.error.message) : "No llego a la vista previa: reintento al seguir escribiendo.") : undefined;
  const invalida = invalidaForzada || (pv !== undefined && !pv.valida);
  const problemas = invalidaForzada ? "Así la ficha pasaría del tope de palabras de su clase: acorta la respuesta." : pv?.problemas.join("; ") ?? pvFallo;
  const bloqueo: "blanco" | "preparando" | "invalida" | null = !listo ? "blanco" : invalida || pvFallo !== undefined ? "invalida" : !pv ? "preparando" : null;

  const acabar = (msg: string) => { setEditando(false); setAsino({ open: false, texto: "" }); say(msg); window.setTimeout(() => estadoRef.current?.focus(), 0); };
  const enviar = async (body: FirmaRequest, msg: string) => {
    if (forzada) { say(FORZADA); return; }
    if (enError) { say(SIN_CONEXION_FIRMA); return; }
    setEnviando(true); setAviso(null);
    try { await firmar.mutateAsync(body); acabar(msg); } catch (e) {
      // 409 `stale` en un criterio «vale»: se rehace la vista previa. Si compone OTRA cosa, la que leíste
      // había caducado (léela y firma); si compone lo mismo, lo que cambió es la ficha (banner de recarga)
      if (e instanceof ApiError && e.status === 409 && e.code === "stale" && body.kind === "criterio" && body.decision === "vale" && body.answers) {
        try {
          const nueva = await vista.mutateAsync(body.answers);
          setPara(JSON.stringify([f.hash, ...body.answers]));
          if (nueva.resultHash !== body.resultHash) setAviso("La vista previa había caducado: vuelve a leerla y firma.");
          else onStale();
        } catch { onStale(); }
      } else trata(e);
    } finally { setEnviando(false); }
  };
  const blancoError = () => {
    const i = limpias.findIndex((a) => !a);
    setErrores(limpias.map((a, j) => (j === i ? "Escribe tu respuesta o pulsa «No lo sé»: en blanco no se firma." : undefined)));
    document.getElementById(`resp-${i}`)?.focus();
  };
  const vale = () => {
    if (bloqueo === "blanco") { blancoError(); return; }
    if (bloqueo === "invalida") { say(problemas ?? "La vista previa no cumple el contrato de ficha."); return; }
    if (bloqueo === "preparando" || !pv) { say("Preparando la vista previa: un momento."); return; }
    // el resultHash de la vista previa viaja con la firma: se firma lo que se leyó (409 `stale` si el servidor compone otra cosa);
    // en el resto de firmas, el mismo hash leído
    void enviar({ kind: "criterio", decision: "vale", answers: limpias, baseHash: f.hash, resultHash: pv.resultHash }, `Firmado. Tu respuesta queda como criterio de la casa (${me.name}).`);
  };
  /**
   * «No lo sé». Con UNA pregunta es una firma en sí (la vista previa no cambia
   * el texto: el marcador se queda). Con varias, solo rellena las que estén en
   * blanco con «no lo sé» y deja que el socio lea la vista previa y firme.
   */
  const noLoSe = async () => {
    if (forzada) { say(FORZADA); return; }
    if (enError) { say(SIN_CONEXION_FIRMA); return; }
    if (n > 1) {
      setAnswers((a) => a.map((x) => (x.trim() ? x : NO_LO_SE)));
      setErrores([]);
      say("Las preguntas en blanco quedan en «no lo sé»: lee la vista previa y firma.");
      return;
    }
    setEnviando(true);
    try {
      const p = await vistaNoSe.mutateAsync([NO_LO_SE]);
      setEnviando(false);
      await enviar({ kind: "criterio", decision: "vale", answers: [NO_LO_SE], baseHash: f.hash, resultHash: p.resultHash }, "Anotado: la pregunta sigue abierta y la ficha, como propuesta de la casa.");
    } catch (e) { setEnviando(false); trata(e); }
  };
  const mandarAsiNo = () => {
    const t = asino.texto.trim();
    if (!t) { setAsino((s) => ({ ...s, error: "Di qué cambiarías: el juez lo necesita para rehacer la ficha." })); asinoRef.current?.focus(); return; }
    void enviar({ kind: "criterio", decision: "asi-no", comment: t, baseHash: f.hash, resultHash: f.hash }, "Enviado. El juez rehará la ficha con tu comentario y te pedirá la firma otra vez.");
  };
  const tomar = async () => {
    if (forzada) { say(FORZADA); return; }
    if (enError) { say(SIN_CONEXION_FIRMA); return; }
    setAviso(null);
    try {
      await dueno.mutateAsync({ code: f.notebook, body: { accion: "tomar" } });
      say(`Eres dueño de ${f.notebook}: ya puedes firmar su criterio.`);
      // el formulario aparece al volver la ficha: el foco, a su primer campo (o a la card si aún no está)
      window.setTimeout(() => (document.getElementById("resp-0") ?? estadoRef.current)?.focus(), 300);
    } catch (e) { trata(e); }
  };

  const titulo = f.puedoFirmar.criterio || mia ? "Tu criterio" : "El criterio";
  const hint = formVis ? "dos minutos" : !f.puedoFirmar.criterio && !mia ? "solo lectura" : "firma del dueño";
  const verPreguntas = !(est.k === "firmada" || est.k === "volcada" || est.k === "en-git") || formVis;
  const dueño = f.dueno && (
    <div className="who"><Avatar size="s">{initials(f.dueno.name)}</Avatar><Prose>Lleva este cuaderno: <b>{f.dueno.name}</b></Prose></div>
  );
  const resp = (as: readonly string[]) => as.map((a, i) => <ProseP key={i}>«{a}»</ProseP>);

  return (
    <Stale stale={enError}>
      <Card flush aria-label={titulo}>
        <CardHeader title={titulo} hint={hint} />
        <CardPad className="side">
          {avisoUi}

          {est.k === "caducada" && !editando && (
            <div className="side">
              <div><Badge tone="warn">Tu firma es de un texto anterior</Badge></div>
              <Prose>Firmaste {elDia(est.firma.createdAt)}. Después el método cambió el texto, así que tu firma ya no vale para esta versión. Tu respuesta de entonces está abajo: confírmala o cámbiala.</Prose>
            </div>
          )}

          {verPreguntas && f.preguntas.length === 1 && <ProseP>{mayuscula(f.preguntas[0]?.texto ?? "")}</ProseP>}

          {formVis && (
            <div className="side">
              {/* ADR-20 pt 7: antes de firmar, la respuesta llega literal a los clientes y vale ADR-17 */}
              <Help id="avisoPublica">{AVISO_PUBLICA}</Help>
              {f.preguntas.map((p, i) => (
                <div className="tight" key={i}>
                  {f.preguntas.length > 1 && <ProseP>{i + 1}. {mayuscula(p.texto)}</ProseP>}
                  <Field id={`resp-${i}`} label={f.preguntas.length > 1 ? `Tu respuesta ${i + 1}` : "Tu respuesta"} required error={errores[i]}>
                    <Textarea id={`resp-${i}`} rows={3} aria-required readOnly={enError} maxLength={ANSWER_MAX}
                      aria-invalid={invalida || errores[i] !== undefined || undefined}
                      aria-describedby={["avisoPublica", invalida || pvFallo !== undefined ? "pv-e" : "", `resp-${i}-e`].filter(Boolean).join(" ")}
                      placeholder={i === 0 ? "p. ej. «Sí, alrededor del 1 %; con dos ferias al año, hasta el 1,3 %.»" : undefined}
                      value={answers[i] ?? ""}
                      onChange={(e) => { const v = e.target.value; setAnswers((a) => a.map((x, j) => (j === i ? v : x))); if (v.trim()) setErrores((er) => er.map((x, j) => (j === i ? undefined : x))); }} />
                  </Field>
                  {f.preguntas.length > 1 && <div><Button variant="link" stale={enError} onClick={() => setAnswers((a) => a.map((x, j) => (j === i ? NO_LO_SE : x)))}>No lo sé en esta</Button></div>}
                </div>
              ))}
              <FieldGroup id="pv" label="Así quedará en la ficha" group error={invalida || pvFallo !== undefined ? problemas : undefined} errorAlert>
                <div>
                  {pv && !invalidaForzada
                    ? <FichaProse bare markdown={parrafosCriterio(pv.body).join("\n\n")} />
                    : limpias.map((a, i) => (
                      <ProseP key={i}><b>Criterio de la casa ({me.name}, {hoy()}):</b> {a || <Fainted>(tu respuesta aparecerá aquí)</Fainted>}</ProseP>
                    ))}
                  {limpias.map((a, i) => (esNoLoSe(a) ? <Whisper key={`n${i}`}>{n > 1 ? `La pregunta ${i + 1}` : "La pregunta"} sigue abierta («no lo sé»): la ficha se queda como propuesta de la casa.</Whisper> : null))}
                </div>
              </FieldGroup>
              <FormActs>
                <Hold label={editando ? "Firmar de nuevo" : "Vale, así lo hacemos"} onHold={vale}
                  // apagada con porqué solo si la vista previa no se puede firmar; en blanco o preparándose, avisa al empezar
                  blocked={bloqueo === "invalida"} onBlocked={vale} antes={() => { if (enviando) return false; if (bloqueo !== null) { vale(); return false; } return true; }}
                  describedBy={invalida || pvFallo !== undefined ? "pv-e valeComo" : "valeComo"} stale={enError} />
                <Button stale={enError} loading={enviando && n === 1} onClick={() => void noLoSe()}>No lo sé</Button>
                <Button variant="quiet" hov="danger" stale={enError} aria-expanded={asino.open} aria-controls="asinoBox"
                  onClick={() => { setAsino((s) => ({ ...s, open: !s.open, error: undefined })); if (!asino.open) window.setTimeout(() => asinoRef.current?.focus(), 0); }}>Así no</Button>
                {editando && <Button variant="ghost" onClick={() => setEditando(false)}>Cancelar</Button>}
              </FormActs>
              <div className="side" id="asinoBox" hidden={!asino.open}>
                <Field id="asinoTxt" label="Qué cambiarías" required error={asino.error}>
                  <Textarea id="asinoTxt" ref={asinoRef} rows={3} aria-required readOnly={enError} aria-invalid={asino.error !== undefined || undefined} aria-describedby="asinoTxt-e"
                    placeholder="p. ej. «En distribución no llega al doble: nosotros nos quedamos en el 1,5 %.»"
                    value={asino.texto} onChange={(e) => { const v = e.target.value; setAsino((s) => ({ ...s, texto: v, error: v.trim() ? undefined : s.error })); }} />
                </Field>
                <FormActs>
                  <Button stale={enError} loading={enviando} onClick={mandarAsiNo}>Enviar «así no»</Button>
                  <Button variant="ghost" onClick={() => setAsino({ open: false, texto: asino.texto })}>Cancelar</Button>
                </FormActs>
              </div>
              <Help id="valeComo">Para firmar, mantén pulsado «{editando ? "Firmar de nuevo" : "Vale, así lo hacemos"}» hasta que se llene; con lector de pantalla, basta con activarlo.</Help>
              <Help>Firmas el texto tal como queda aquí; si la ficha cambia después, te la volveremos a pedir. «No lo sé» también es una respuesta: la pregunta sigue abierta y la ficha, como propuesta de la casa.{n > 1 ? " Con varias preguntas, «No lo sé» deja abiertas las que estén en blanco." : ""}</Help>
            </div>
          )}

          <div ref={estadoRef} tabIndex={-1} className="side">
            {(est.k === "firmada" || est.k === "volcada") && !formVis && (mia ? (
              <>
                <div><Badge tone="ok">criterio de la casa</Badge></div>
                {resp(est.firma.answers)}
                <Help>Firmado por {est.firma.signer.name} · {cuando(est.firma.createdAt)}</Help>
                <Help>{est.firma.volcado === "volcada" ? "Volcada a git: la ficha ya es de la casa y su texto recoge tu respuesta."
                  : est.firma.volcado === "en-pr" ? "En un PR del volcado: cuando se fusione, la ficha es de la casa."
                  : "Pendiente de volcar a git: hasta entonces, en git la ficha sigue revisada por IA."}</Help>
                <div className="tight">
                  <div>
                    <Button variant="link" stale={enError} aria-disabled={est.k === "volcada" || undefined} aria-describedby={est.k === "volcada" ? "cambiarWhy" : undefined}
                      onClick={() => { if (est.k === "volcada") say("Ya está en git: para cambiarlo, pídelo al juez."); else if (!f.puedoFirmar.criterio) say(f.puedoFirmar.porque.criterio ?? "Ahora no puedes cambiarla."); else { setAnswers(respuestas(est, n)); setEditando(true); } }}>Cambiar mi respuesta</Button>
                  </div>
                  {est.k === "volcada" && <Help id="cambiarWhy">Ya está en git: para cambiarlo, pídelo al juez.</Help>}
                </div>
              </>
            ) : (
              <>
                {dueño}
                <div><Badge tone="ok">criterio de la casa ({est.firma.signer.name})</Badge></div>
                {resp(est.firma.answers)}
                <Help>Contestó {elDia(est.firma.createdAt)} y {est.firma.volcado === "volcada" ? "está volcado a git: la ficha ya es de la casa." : "está pendiente de volcar a git."} Solo firma el criterio quien es dueño del cuaderno; tú puedes darla por buena en «Revisión».</Help>
              </>
            ))}

            {est.k === "nolose" && !formVis && (
              <>
                <div><Badge>pregunta abierta</Badge></div>
                <Help>{mia ? "Contestaste" : `${est.firma.signer.name} contestó`} «no lo sé» · {cuando(est.firma.createdAt)}. La pregunta sigue abierta y la ficha sigue como propuesta de la casa.</Help>
                {mia && f.puedoFirmar.criterio && <div><Button variant="link" stale={enError} onClick={() => { setAnswers(respuestas(est, n)); setEditando(true); }}>Cambiar mi respuesta</Button></div>}
              </>
            )}

            {est.k === "asino" && (
              <>
                <div className="who"><Badge tone="danger">«así no» enviado</Badge><Badge>pendiente del juez</Badge></div>
                {est.firma.comment && <ProseP>«{est.firma.comment}»</ProseP>}
                <Help>Enviado por {est.firma.signer.name} · {cuando(est.firma.createdAt)}. {est.mia ? "El juez la vuelve a pasar por los críticos con tu comentario y te pedirá la firma otra vez; no tienes que hacer nada más." : "El juez la vuelve a pasar por los críticos; hasta entonces nadie firma el criterio encima."}</Help>
              </>
            )}

            {est.k === "en-git" && (
              <>
                {dueño}
                <div><Badge tone="ok">{f.houseCriteriaBy ? `criterio de la casa (${f.houseCriteriaBy})` : "criterio de la casa"}</Badge></div>
                <FichaProse bare markdown={parrafosCriterio(f.body).join("\n\n")} />
                <Help>Está en git: la ficha ya es de la casa. Solo firma el criterio quien es dueño del cuaderno; tú puedes darla por buena en «Revisión».</Help>
              </>
            )}

            {est.k === "no-dueno" && (
              <>
                {dueño}
                <Help>{est.porque}</Help>
                {est.sinDueno && <div><Button stale={enError} loading={dueno.isPending} onClick={() => void tomar()}>Hacerme dueño de {f.notebook}</Button></div>}
              </>
            )}
          </div>
        </CardPad>
      </Card>
    </Stale>
  );
}

// ── 2 · Revisión, despacho y resolución ───────────────────────────────

function Revision({ api, f, me, enError, forzada, onStale }: Comun) {
  const say = useToast();
  const firmar = useFirmar(api, f.slug);
  const { setAviso, trata, avisoUi } = useErrores(onStale);
  const mia = f.firmas.find((x) => x.kind === "revision" && x.decision === "vale" && x.estado === "vigente" && x.signer.oid === me.oid);
  const despacho = f.firmas.find((x) => x.kind === "despacho" && x.decision === "vale" && x.estado === "vigente");
  const porque = f.status === "reviewed" || f.status === "published" ? "Ya está revisada por un socio."
    : f.status !== "house" ? "Se puede revisar cuando el criterio esté volcado a git (de la casa)."
    : porqueHumano(f.puedoFirmar.porque.revision ?? "Ahora no puedes revisarla.");
  const ref = useRef<HTMLDivElement>(null);
  const dar = async () => {
    if (forzada) { say(FORZADA); return; }
    if (enError) { say(SIN_CONEXION_FIRMA); return; }
    setAviso(null);
    try {
      await firmar.mutateAsync({ kind: "revision", decision: "vale", baseHash: f.hash, resultHash: f.hash });
      say("Revisión firmada. Cuando se vuelque a git, la ficha pasa a revisada por un socio.");
      window.setTimeout(() => ref.current?.focus(), 0);
    } catch (e) { trata(e); }
  };
  return (
    <Stale stale={enError}>
      <Card flush aria-label="Revisión">
        <CardHeader title="Revisión" hint="un segundo socio lee la ficha entera" />
        <CardPad className="side">
          {avisoUi}
          <Prose>Se revisa la ficha ya de la casa. La da un socio que no sea <b>{f.curator}</b> (quien curó la ficha) ni quien firmó el criterio.</Prose>
          {mia ? (
            <div className="tight" ref={ref} tabIndex={-1}>
              <div><Badge tone="ok">revisada por ti</Badge></div>
              <Help>{mia.volcado === "volcada" ? "Volcada a git: la ficha está revisada por un socio." : `La diste por buena ${elDia(mia.createdAt)}. Cuando se vuelque a git, la ficha pasa a revisada por un socio.`}</Help>
            </div>
          ) : f.puedoFirmar.revision ? (
            <div><Button variant="primary" stale={enError} loading={firmar.isPending} onClick={() => void dar()}>La doy por buena</Button></div>
          ) : (
            <div className="tight">
              <div><Button aria-disabled aria-describedby="revWhy" tip={porque.replace(/\.$/, "")} tipPos="left" stale={enError} onClick={() => say(porque)}>La doy por buena</Button></div>
              <Help id="revWhy">{porque}</Help>
            </div>
          )}
          <Help>{!f.professionalReview ? "Visto del despacho: no aplica a este cuaderno."
            : despacho ? `Visto del despacho: registrado${despacho.reference ? ` (${despacho.reference})` : ""}.`
            : "Visto del despacho: pendiente; lo registra el Skein Team con la referencia del despacho."}</Help>
        </CardPad>
      </Card>
    </Stale>
  );
}

function Despacho({ api, f, enError, forzada, onStale }: Comun) {
  const say = useToast();
  const firmar = useFirmar(api, f.slug);
  const { setAviso, trata, avisoUi } = useErrores(onStale);
  const [ref, setRef] = useState("");
  const [err, setErr] = useState<string | undefined>(undefined);
  const registrar = async () => {
    if (forzada) { say(FORZADA); return; }
    if (enError) { say(SIN_CONEXION_FIRMA); return; }
    if (!ref.trim()) { setErr("La referencia del visto es obligatoria: es lo que queda en git."); return; }
    setAviso(null);
    try {
      await firmar.mutateAsync({ kind: "despacho", decision: "vale", reference: ref.trim(), baseHash: f.hash, resultHash: f.hash });
      setRef(""); say("Visto del despacho registrado."); focoFirmas();
    } catch (e) { trata(e); }
  };
  return (
    <Stale stale={enError}>
      <Card flush aria-label="Visto del despacho">
        <CardHeader title="Visto del despacho" hint="lo registra el Skein Team" />
        <CardPad className="side">
          {avisoUi}
          {f.puedoFirmar.despacho ? (
            <>
              <TextField id="despRef" label="Referencia del visto" required error={err} value={ref} readOnly={enError}
                placeholder="p. ej. «Correo de Ríos Asesores, 4 nov, asunto: C07 fichas 1–5»" onChange={(e) => { setRef(e.target.value); if (e.target.value.trim()) setErr(undefined); }} />
              <FormActs><Button stale={enError} loading={firmar.isPending} onClick={() => void registrar()}>Registrar el visto</Button></FormActs>
            </>
          ) : <Help>{f.status !== "house" ? "Se registra cuando el criterio esté volcado a git (de la casa)." : porqueHumano(f.puedoFirmar.porque.despacho ?? "Ahora no se puede registrar.")}</Help>}
        </CardPad>
      </Card>
    </Stale>
  );
}

function Resolver({ api, f, enError, forzada, onStale, firma }: Comun & { firma: Firma }) {
  const say = useToast();
  const resolver = useResolver(api, f.slug);
  const { setAviso, trata, avisoUi } = useErrores(onStale);
  const [nota, setNota] = useState("");
  const [err, setErr] = useState<string | undefined>(undefined);
  const id = `res-${firma.id}`;
  const cerrar = async () => {
    if (forzada) { say(FORZADA); return; }
    if (enError) { say(SIN_CONEXION_FIRMA); return; }
    if (!nota.trim()) { setErr("Di por qué se cierra: queda en el registro del «así no»."); return; }
    setAviso(null);
    // al resolver, esta card desaparece: el foco va a «Firmas», donde queda la resolución
    try { await resolver.mutateAsync({ signoffId: firma.id, note: nota.trim() }); say("«Así no» resuelto: la ficha vuelve a pedir la firma."); focoFirmas(); } catch (e) { trata(e); }
  };
  return (
    <Stale stale={enError}>
      <Card flush aria-label="Resolver «así no»">
        <CardHeader title="Resolver «así no»" hint={`${claseFirma(firma)} · Skein Team`} />
        <CardPad className="side">
          {avisoUi}
          <Prose><b>{firma.signer.name}</b> · {cuando(firma.createdAt)}</Prose>
          {firma.comment && <ProseP>«{firma.comment}»</ProseP>}
          <Field id={id} label="Nota de la resolución" required error={err} help="Ciérralo solo si el texto se sostiene; si no, el juez corrige por el método y la firma caduca sola.">
            <Textarea id={id} rows={3} aria-required readOnly={enError} aria-invalid={err !== undefined || undefined} aria-describedby={`${id}-h ${id}-e`}
              value={nota} onChange={(e) => { setNota(e.target.value); if (e.target.value.trim()) setErr(undefined); }} />
          </Field>
          <FormActs><Button stale={enError} loading={resolver.isPending} onClick={() => void cerrar()}>Cerrar con esta nota</Button></FormActs>
        </CardPad>
      </Card>
    </Stale>
  );
}

// ── 3 · Firmas ────────────────────────────────────────────────────────

function lineaEstado(x: Firma): ReactNode {
  if (x.decision === "asi-no") {
    return x.resolucion
      ? <><Badge>resuelto</Badge> <Fainted>por {x.resolucion.by.name}, {cuando(x.resolucion.createdAt)}: {x.resolucion.note}</Fainted></>
      : x.estado === "caducada" ? <><Badge tone="warn">caducada</Badge> <Fainted>el texto cambió después</Fainted></>
      : <><Badge tone="danger">así no</Badge> <Fainted>pendiente del juez</Fainted></>;
  }
  if (x.estado === "caducada") return <><Badge tone="warn">caducada</Badge> <Fainted>el texto cambió después</Fainted></>;
  if (x.estado === "sustituida") return <Fainted>sustituida por una firma nueva</Fainted>;
  if (decisionFirma(x) === "no lo sé") return <><Badge>vigente</Badge> <Fainted>la pregunta sigue abierta y la ficha sigue como propuesta</Fainted></>;
  const v = x.volcado === "volcada" ? "volcada a git" : x.volcado === "en-pr" ? "en un PR del volcado" : "pendiente de volcar a git";
  return <><Badge tone="ok">vigente</Badge> {x.volcado === "en-pr" && x.prUrl && PR_URL.test(x.prUrl) ? <a className={buttonClass({ variant: "link" })} href={x.prUrl} target="_blank" rel="noopener noreferrer">{v}</a> : <Fainted>{v}</Fainted>}</>;
}

function Firmas({ f }: { f: FichaData }) {
  const xs = [...f.firmas].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return (
    <Card flush aria-label="Firmas" id={FIRMAS_ID} tabIndex={-1}>
      <CardHeader title="Firmas" hint={<>texto actual <Mono>{hash8(f.hash)}</Mono></>} />
      {xs.length ? (
        <Feed full>
          {xs.map((x) => (
            <FeedItem key={x.id} t={cuando(x.createdAt)}>
              <FeedWho>{claseFirma(x)}</FeedWho> <b>{x.signer.name}</b> · {decisionFirma(x)}<br />
              <Mono>leído {hash8(x.baseHash)}{x.resultHash !== x.baseHash ? ` → firmado ${hash8(x.resultHash)}` : ""}</Mono><br />
              {lineaEstado(x)}
            </FeedItem>
          ))}
        </Feed>
      ) : (
        <div className="feed-empty">
          <Empty inline title="Aún no hay firmas de esta ficha.">
            {f.status === "reviewed-ai" ? "En git sigue revisada por IA: la escribió la IA y la pasaron los críticos; falta el criterio de la casa." : `Estado en git: ${ESTADO_GIT[f.status].label}.`}
          </Empty>
        </div>
      )}
    </Card>
  );
}

// ── La pantalla ───────────────────────────────────────────────────────

/** `telemetria`: los contadores de uso (ADR-20 pt 4) para «Citas en el Despacho: N» en la cabecera; sin él, no se pide ni se pinta. */
export function Ficha({ api, telemetria, slug, estado, firma }: { api: BibliotecaApi; telemetria?: TelemetriaApi | undefined; slug: string; estado?: string | undefined; firma?: string | undefined }) {
  const q = useFichaDe(api, slug);
  const uso = useTelemetriaDe(telemetria);
  // «Citas en el Despacho: N», solo si por ficha se enseña (rol completo; pre, o cinco casas) y la API la trae entre las más citadas
  const citada = uso.data?.porFichaVisible ? (uso.data.masCitadas.find((x) => x.slug === slug)?.veces ?? 0) : 0;
  const b = useBibliotecaDe(api);
  const real = q.data;
  const cuaderno = useCuadernoDe(api, real?.notebook);
  const [tab, setTab] = useState<"ficha" | "fuentes" | "historial">("ficha");
  const [cambiada, setCambiadaState] = useState(false);
  const say = useToast();
  // el banner de «la ficha ha cambiado» vive en una región `role=status` montada siempre (se anuncia) y recibe el foco
  const regionCambio = useRef<HTMLDivElement>(null);
  const setCambiada = (v: boolean) => { setCambiadaState(v); if (v) window.setTimeout(() => regionCambio.current?.focus(), 0); };
  const biblioteca: Biblioteca | undefined = b.data;
  const me = biblioteca ? { oid: biblioteca.me.oid, name: biblioteca.me.name } : { oid: "", name: "" };
  const forzadaFirma = esFirmaForzada(firma) ? firma : undefined;
  const f = real && forzadaFirma && biblioteca ? simularFirma(real, me, forzadaFirma) : real;
  const forced = estado === "cargando" || estado === "error" || estado === "bloqueada" ? estado : undefined;
  const notFound = q.error instanceof ApiError && q.error.status === 404;
  const sinRol = q.error instanceof ApiError && q.error.status === 403;
  const view = forced === "cargando" ? "cargando"
    // sin `me` aún (la biblioteca da quién soy), una firma mía se pintaría como ajena: se espera
    : q.isPending || b.isPending ? "cargando" : notFound ? "no-existe" : sinRol ? "sin-rol" : q.isError && !q.data ? "error-sin-datos" : forced === "error" || q.isError ? "error" : "detalle";
  const enError = view === "error";
  const nb = cuaderno.data?.cuaderno;

  useAsideSlot(<MiRevision api={api} biblioteca={biblioteca} cargando={b.isPending} sinConexion={view === "error" || view === "error-sin-datos"} />);
  useCrumbs([
    <Link key="l" to="/conocimiento">Conocimiento</Link>,
    ...(f ? [<Link key="c" to="/conocimiento" search={{ q: f.notebook }}>{f.notebook}{nb ? ` · ${nb.title}` : ""}</Link>] : []),
    <Mono key="s">{slug}</Mono>,
  ], `${slug}|${f?.notebook ?? ""}|${nb?.title ?? ""}`);

  if (view === "cargando") return <Cargando />;
  if (view === "no-existe") return (
    <Empty glyph="✳" title={`No hay ficha «${slug}» en la biblioteca`}>
      Quizá cambió de nombre o el enlace es viejo.
      <Link to="/conocimiento" className={buttonClass({ variant: "primary" })}>Volver a la biblioteca</Link>
    </Empty>
  );
  if (view === "sin-rol") return <Empty glyph="✳" title="Tu cuenta no tiene el rol de biblioteca">Con él lees las fichas y firmas el criterio de tus cuadernos. Pídeselo a un admin de Skein.</Empty>;
  if (view === "error-sin-datos" || !f) return (
    <Banner tone="danger"><b>No llego a la biblioteca.</b> Y aún no tengo la ficha en memoria. Reintento solo cada 30 s. <Button variant="link" onClick={() => void q.refetch()}>reintentar ahora</Button></Banner>
  );

  const bloqueada = forced === "bloqueada" || f.status === "blocked" || f.status === "draft";
  const git = ESTADO_GIT[bloqueada ? "blocked" : f.status];
  const clase = CLASE[f.kind] ?? { label: f.kind, tip: `Clase de la ficha: ${f.kind}` };
  const porVolcar = f.firmas.filter((x) => x.decision === "vale" && x.estado === "vigente" && (x.volcado === "pendiente" || x.volcado === "en-pr")).length;
  const fichas = cuaderno.data?.fichas ?? [];
  const i = fichas.findIndex((x) => x.slug === f.slug);
  const prev = i > 0 ? fichas[i - 1] : undefined;
  const next = i >= 0 && i < fichas.length - 1 ? fichas[i + 1] : undefined;
  // solo se enlazan las relacionadas que existen en el catálogo, con su título (de cualquier cuaderno)
  const titulos = new Map(f.relacionadas.map((x) => [x.slug, x.title]));
  const rel = new Set(titulos.keys());
  const completo = biblioteca?.me.rol === "completo";
  const asinosAbiertos = f.firmas.filter((x) => x.decision === "asi-no" && x.estado !== "caducada" && !x.resolucion);
  const comun: Comun = { api, f, me, enError, forzada: forzadaFirma !== undefined, onStale: () => setCambiada(true) };
  const recargar = () => { setCambiada(false); void q.refetch(); };
  const marcador = (n: number) => (
    <Whisper>Aquí entrará el criterio de la casa cuando el dueño del cuaderno conteste {f.preguntas.length > 1 ? `la pregunta ${n + 1}` : "la pregunta"} de esta ficha («Tu criterio»).</Whisper>
  );

  return (
    <>
      {enError && (
        <Banner tone="danger" className="banner-gap">
          <b>No llego a la biblioteca.</b> Lo que ves puede estar desactualizado y ahora no se puede firmar: tu respuesta no se pierde, la guardo aquí. Reintento solo cada 30 s.{" "}
          <Button variant="link" onClick={() => void q.refetch()}>reintentar ahora</Button>
        </Banner>
      )}
      <LiveRegion ref={regionCambio}>
        {cambiada && (
          <Banner tone="warn" className="banner-gap">
            <b>La ficha ha cambiado desde que la abriste.</b> Vuelve a leerla antes de firmar: tu respuesta no se pierde. <Button variant="link" onClick={recargar}>volver a cargar</Button>
          </Banner>
        )}
      </LiveRegion>
      {bloqueada && (
        <Banner tone="warn" className="banner-gap">
          <b>Esta ficha espera a una persona:</b> le falta una lectura humana de su fuente antes de pedir el criterio de la casa. Se lee entera, pero no se firma hasta entonces; te avisaremos en «Mi revisión» cuando esté lista.
        </Banner>
      )}
      <PageHead
        title={f.title}
        meta={<>
          <Fainted className="sk-mono" style={{ fontSize: "var(--sk-fs-2xs)", alignSelf: "center" }}>{f.notebook} · {f.slug}</Fainted>
          <Badge tip={clase.tip}>{clase.label}</Badge>
          <Badge tone={git.tone} tip={`Estado de la ficha en git: ${git.label}`}>{git.label}</Badge>
          {porVolcar > 0 && <Badge>{porVolcar} {porVolcar > 1 ? "firmas por volcar a git" : "firma por volcar a git"}</Badge>}
          {f.validUntil && <Badge>caduca el {fechaDia(f.validUntil)}</Badge>}
          {f.relatedSkills.length > 0 && <Badge tip={f.relatedSkills.map(sinGuiones).join(" · ")}>Skills que la citan: {f.relatedSkills.length}</Badge>}
          {citada > 0 && <Badge tip="Veces que el asesor la citó en el Despacho: un contador sin casa, sin texto y sin nombres">Citas en el Despacho: {citada}</Badge>}
        </>}
        actions={<>
          {prev
            ? <Tip tip={prev.title}><Link to="/conocimiento/$slug" params={{ slug: prev.slug }} className={buttonClass({ variant: "ghost" })} aria-label={`Ficha anterior: ${prev.title}`}><Icon name="chevron-l" />Anterior</Link></Tip>
            : <Button variant="ghost" aria-disabled tip="Es la primera ficha del cuaderno" onClick={() => say("Es la primera ficha del cuaderno.")}><Icon name="chevron-l" />Anterior</Button>}
          {next
            ? <Tip tip={next.title}><Link to="/conocimiento/$slug" params={{ slug: next.slug }} className={buttonClass({ variant: "ghost" })} aria-label={`Ficha siguiente: ${next.title}`}>Siguiente<Icon name="chevron-r" /></Link></Tip>
            : <Button variant="ghost" aria-disabled tip="Es la última ficha del cuaderno" onClick={() => say("Es la última ficha del cuaderno.")}>Siguiente<Icon name="chevron-r" /></Button>}
          <Link to="/conocimiento" search={{ q: f.notebook }} className={buttonClass({})}>Volver al cuaderno</Link>
        </>}
      />

      <div className="cols cols--ficha">
        <div className="stack">
          <Card flush aria-label="La ficha">
            <Tabs label="La ficha" style={{ padding: "0 var(--sk-sp-3)" }}>
              <Tab id="tFicha" controls="pFicha" selected={tab === "ficha"} onClick={() => setTab("ficha")}>Ficha</Tab>
              <Tab id="tFuentes" controls="pFuentes" selected={tab === "fuentes"} onClick={() => setTab("fuentes")} mod={f.sources.length}>Fuentes</Tab>
              <Tab id="tHistorial" controls="pHistorial" selected={tab === "historial"} onClick={() => setTab("historial")} mod={f.reviewLog.length}>Historial</Tab>
            </Tabs>
            <TabPanel id="pFicha" labelledBy="tFicha" hidden={tab !== "ficha"}>
              <CardPad>
                <FichaProse className="lectura" sinTitulo markdown={f.body} slugs={rel}
                  renderSlug={(s) => <Link to="/conocimiento/$slug" params={{ slug: s }}>{inicialMinuscula(titulos.get(s) ?? sinGuiones(s))}</Link>}
                  marcador={(n) => marcador(n)} />
              </CardPad>
            </TabPanel>
            <TabPanel id="pFuentes" labelledBy="tFuentes" hidden={tab !== "fuentes"}>
              <CardPad className="side">
                {f.sources.map((s) => (
                  <ItemRow key={s.id} icon="doc"
                    title={<><Fainted className="sk-mono">{s.id}</Fainted> {s.title} <Badge tone={s.license === "oficial" ? "ok" : undefined}>{licencia(s.license)}</Badge></>}
                    meta={[s.author, s.year, s.aiOptout ? "fuente con reserva de IA: leída a mano" : "se puede usar con IA"].filter(Boolean).join(" · ")}
                    act={s.url && /^https?:\/\//i.test(s.url) ? <a className={buttonClass({ variant: "link" })} href={s.url} target="_blank" rel="noopener noreferrer">abrir la fuente</a> : undefined} />
                ))}
                {f.sources.length === 0 && <Empty inline title="Esta ficha no cita fuentes.">Lo que sostiene es criterio propio de la casa.</Empty>}
                <Help>Autoría propia, {f.intake === "manual" ? "escrita a mano" : "escrita con ayuda de IA"} y curada por {f.curator}. La licencia de cada fuente sigue el semáforo de la biblioteca.</Help>
              </CardPad>
            </TabPanel>
            <TabPanel id="pHistorial" labelledBy="tHistorial" hidden={tab !== "historial"}>
              <CardHeader title="Revisiones del método" hint="solo añade — nunca reescribe" />
              {f.reviewLog.length ? (
                <Feed full>
                  {[...f.reviewLog].reverse().map((r, k) => {
                    const v = veredicto(r.verdict);
                    return (
                      <FeedItem key={k} t={r.round !== null ? `ronda ${r.round}` : "—"}>
                        <FeedWho>{r.critic}</FeedWho> <Badge tone={v.tone}>{v.label}</Badge>
                        {r.notes && <> {notaConEnlaces(r.notes, titulos)}</>}
                        {r.hash && <> <Mono>{hash8(r.hash)}</Mono></>}
                      </FeedItem>
                    );
                  })}
                </Feed>
              ) : (
                <div className="feed-empty"><Empty inline title="Sin rondas del método.">Esta ficha aún no ha pasado por los críticos.</Empty></div>
              )}
            </TabPanel>
          </Card>
        </div>

        <div className="stack firma-col">
          {!bloqueada && <TuCriterio {...comun} invalidaForzada={forzadaFirma === "invalida"} key={`${f.slug}|${forzadaFirma ?? ""}`} />}
          {!bloqueada && <Revision {...comun} />}
          {!bloqueada && completo && f.professionalReview && <Despacho {...comun} />}
          {completo && asinosAbiertos.map((x) => <Resolver key={x.id} {...comun} firma={x} />)}
          <Firmas f={f} />
        </div>
      </div>
    </>
  );
}
